import React, { useMemo, useState } from 'react';
import Chart from 'react-apexcharts';

import { useGetIptvReportedIpsPerAppQuery } from '../../redux/apis/analyticsManagement/aMIptvAnalyticsApiSlice';
import getReportedStreamsChartOptions from './charts/reportedStreamsOptions';
import ComponentLoader from '../other/ComponentLoader';
import { Card, CardBody, CardExpandToggler } from '../other/card/card';

const PERIOD_LABELS = {
  current_year: 'Current Year',
  current_month: 'Current Month',
  previous_year: 'Previous Year',
};
const PERIOD_OPTIONS = ['current_year', 'current_month', 'previous_year'];

const CHART_HEIGHT = 350;

const buildChartData = dataByApp => {
  const entries = Object.entries(dataByApp || {});
  if (!entries.length) {
    return { categories: [], series: [] };
  }

  const categories = [...new Set(entries.flatMap(([, monthMap]) => Object.keys(monthMap || {})))].sort((a, b) =>
    a.localeCompare(b)
  );

  const series = entries.map(([appName, monthMap]) => ({
    name: appName,
    data: categories.map(month => Number(monthMap?.[month]) || 0),
  }));

  return { categories, series };
};

const IPTVReportedServers = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('current_year');
  const [isExpanded, setIsExpanded] = useState(false);

  const queryParams = useMemo(() => ({ period: selectedPeriod }), [selectedPeriod]);
  const { data, isLoading, isFetching, isError, error } = useGetIptvReportedIpsPerAppQuery(queryParams, {
    refetchOnMountOrArgChange: true,
  });

  const safeData = useMemo(() => (data && typeof data === 'object' ? data : {}), [data]);

  const hasData = Object.keys(safeData).length > 0;
  const { categories, series } = useMemo(() => buildChartData(safeData), [safeData]);

  const chartOptions = useMemo(() => getReportedStreamsChartOptions(categories), [categories]);
  const getPeriodButtonClass = period =>
    `btn btn-sm bg-transparent ${selectedPeriod === period ? 'text-theme border-theme' : 'text-body-secondary border-secondary opacity-75'}`;

  return (
    <div className='row g-2 mt-1'>
      <div className='col-12'>
        <Card className='mb-0'>
          <CardBody className='relative p-0 d-flex flex-column'>
            {isError ? (
              <div className='absolute inset-0 d-flex items-center justify-center p-3 text-danger small bg-white bg-opacity-75'>
                Failed to load.
                {error?.data?.message ? ` ${error.data.message}` : ''}
              </div>
            ) : null}

            <div className='p-4 d-flex flex-column gap-2'>
              <div className='d-flex align-items-start justify-content-between gap-2'>
                <div className='d-flex fw-bold small'>IPTV Reported Streaming Servers (Domains/IPs)</div>
                <div className='align-self-start'>
                  <CardExpandToggler onExpandChange={setIsExpanded} />
                </div>
              </div>
              <div className='d-flex flex-wrap align-items-center gap-2'>
                {PERIOD_OPTIONS.map(period => (
                  <button
                    key={period}
                    type='button'
                    className={getPeriodButtonClass(period)}
                    disabled={isFetching}
                    onClick={() => setSelectedPeriod(period)}
                  >
                    {PERIOD_LABELS[period]}
                  </button>
                ))}
              </div>
            </div>

            <div
              className='p-4 pt-0'
              style={isExpanded ? { flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' } : {}}
            >
              <div className='small text-body-secondary' style={isExpanded ? { width: '100%' } : {}}>
                <div
                  className='mt-2 position-relative'
                  style={{ height: CHART_HEIGHT }}
                >
                  {isLoading ? <ComponentLoader /> : null}
                  {hasData ? (
                    <Chart type='area' height={CHART_HEIGHT} options={chartOptions} series={series} />
                  ) : !isLoading ? (
                    <div className='d-flex text-base h-100 align-items-center justify-content-center text-center px-2'>
                      No reported IPs data found for this period.
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          </CardBody>
        </Card>
      </div>
    </div>
  );
};

export default IPTVReportedServers;
